import React, { useRef, useState, useMemo, useEffect } from 'react';
import './InputBar.css';
import { audioService } from '../../services/audioService';
import { conversationService } from '../../services/conversationService';
import { useConversation } from '../../state/ConversationContext';
import { useAuth } from '../../state/AuthContext';
import { Square } from 'lucide-react';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

const MAX_RECORDING_MS = 120000;

export default function InputBar() {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const recorderRef = useRef(null); // holds { stop }
  const recordingTimeoutRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const textareaRef = useRef(null); // holds textarea DOM element
  const { sendText, draftText, setDraftText } = useConversation();
  const { billingStatus } = useAuth();
  const text = draftText;
  const setText = setDraftText;
  const trialMessageLimitReached = billingStatus?.billing_status === 'trialing' && billingStatus?.message_limit_reached;

  const status = useMemo(() => {
    return sending
    ? 'Sending…'
    : transcribing
    ? 'Transcribing…'
    : recording
    ? 'Recording…'
    : text
    ? 'Text ready'
    : 'Idle';
  }, [sending, transcribing, recording, text]);

  // Auto-resize textarea as content changes
  const handleInput = (e) => {
    const textarea = e.target;
    if (!textarea) return;

    // Reset height to auto to get accurate scrollHeight
    textarea.style.height = 'auto';

    // Set height to scrollHeight, capped at max-height (120px)
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = newHeight + 'px';
  };

  // Reset textarea height when text is cleared
  useEffect(() => {
    if (!text && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [text]);

  useEffect(() => {
    return () => {
      if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    };
  }, []);

  const stopRecordingAndTranscribe = async (hitLimit = false) => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    const startedAt = recordingStartedAtRef.current;
    recordingStartedAtRef.current = null;
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    setRecording(false);

    if (!rec) return;
    if (hitLimit) {
      setNotice({
        title: 'Recording stopped',
        message: 'Voice messages are limited to 2 minutes. We’ll transcribe what you recorded.',
        severity: 'info',
      });
    }

    const blob = await rec.stop();
    const durationSeconds = startedAt
      ? Math.min(Math.ceil((Date.now() - startedAt) / 1000), MAX_RECORDING_MS / 1000)
      : 0;

    // Auto-send to backend for transcription
    setTranscribing(true);
    try {
      const transcript = await conversationService.transcribeAudio(blob, durationSeconds);

      // Concatenate to existing text with a space (if there's existing text)
      setText((prev) => {
        const currentText = prev.trim();
        return currentText ? `${currentText} ${transcript}` : transcript;
      });

      // Auto-resize the textarea after setting text
      if (textareaRef.current) {
        setTimeout(() => {
          const textarea = textareaRef.current;
          if (textarea) {
            textarea.style.height = 'auto';
            const newHeight = Math.min(textarea.scrollHeight, 120);
            textarea.style.height = newHeight + 'px';
          }
        }, 0);
      }
    } catch (transcriptionError) {
      console.error('Transcription error:', transcriptionError);
      setNotice(getUserFacingError(transcriptionError, 'transcription'));
    } finally {
      setTranscribing(false);
    }
  };

  const onMicClick = async () => {
    try {
      setNotice(null);
      if (!recording) {
        // Start a fresh recording
        const rec = await audioService.startRecording();
        recorderRef.current = rec;
        recordingStartedAtRef.current = Date.now();
        setRecording(true);
        recordingTimeoutRef.current = setTimeout(() => {
          stopRecordingAndTranscribe(true);
        }, MAX_RECORDING_MS);
      } else {
        // Stop recording and auto-transcribe
        await stopRecordingAndTranscribe();
      }
    } catch (e) {
      console.error('Mic error:', e);
      setNotice(getUserFacingError(e, 'transcription'));
      setRecording(false);
      setTranscribing(false);
      recorderRef.current = null;
    }
  };

  // Sending is delegated to ConversationContext

  const onSend = async () => {
    if (sending || !text.trim() || trialMessageLimitReached) return;

    setSending(true);
    const text_holder = text.trim();
    try {
      setNotice(null);
      setText('');
      await sendText(text_holder);
    } catch (e) {
      console.error('Send failed:', e);
      setText(text_holder);
    } finally {
      setSending(false);
    }
  };

  const canSend = useMemo(() => {
    return !!text.trim() && !trialMessageLimitReached;
  }, [text, trialMessageLimitReached]);

  return (
    <div className="inputbar" role="form" aria-label="Message input">
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">{status}</div>
     
      <div className="input-row">
        <button
          className={`mic ${recording ? 'on' : ''}`}
          aria-pressed={recording}
          aria-label={recording ? 'Stop recording and transcribe' : 'Start recording'}
          onClick={onMicClick}
          disabled={sending || transcribing}
        >
          {recording ? <Square size={20} /> : (
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
              <rect x="5" y="1" width="8" height="11" rx="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 9.5C2 13.09 5.13 16 9 16C12.87 16 16 13.09 16 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="9" y1="16" x2="9" y2="19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          )}
        </button>
        <textarea
          ref={textareaRef}
          className="text"
          placeholder={trialMessageLimitReached ? 'Upgrade to keep chatting' : (transcribing ? 'Transcribing…' : (sending ? '' : 'Ask anything about your money…'))}
          aria-label="Ask a question"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onInput={handleInput}
          disabled={transcribing || trialMessageLimitReached}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !sending && !transcribing && !trialMessageLimitReached) {
              e.preventDefault();
              onSend();
            }
          }}
          rows={1}
        />
        <button className="send" onClick={onSend} disabled={!canSend || sending || transcribing} aria-disabled={!canSend || sending || transcribing} aria-label="Send message">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 13V3M3 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      {notice && (
        <InlineAlert
          compact
          title={notice.title}
          message={notice.message}
          tone={notice.severity === 'info' ? 'info' : 'error'}
          actionLabel="Dismiss"
          onAction={() => setNotice(null)}
        />
      )}
      <div className="mt-legal">Informational only • Not financial advice</div>
    </div>
  );
}
